import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { galleryService } from '../services/gallery'
import type {
  Gallery,
  GalleryFolderResponse,
  GalleryDetail,
  UpdateGallery,
  UpdateGalleryStatus,
  GalleryStats,
  PaginationParams,
  ApiResponse,
  PaginatedResponse
} from '../types'

// Query keys
export const galleryKeys = {
  all: ['galleries'] as const,
  lists: () => [...galleryKeys.all, 'list'] as const,
  list: (params: PaginationParams) => [...galleryKeys.lists(), params] as const,
  filters: () => [...galleryKeys.all, 'filter'] as const,
  filter: (params: PaginationParams, filters: Record<string, any>) =>
    [...galleryKeys.filters(), params, filters] as const,
  details: () => [...galleryKeys.all, 'detail'] as const,
  detail: (id: string) => [...galleryKeys.details(), id] as const,
  folders: () => [...galleryKeys.all, 'folder'] as const,
  folder: (id: string) => [...galleryKeys.folders(), id] as const,
  stats: () => [...galleryKeys.all, 'stats'] as const
}

// Hook to fetch galleries with pagination
export function useGalleries(params: PaginationParams = { page: 1, size: 10 }) {
  return useQuery<ApiResponse<GalleryFolderResponse[]>, Error>({
    queryKey: galleryKeys.list(params),
    queryFn: () => galleryService.getGalleries(params)
  })
}

// Hook to filter galleries (admin)
export function useFilterGalleries(
  params: PaginationParams = { page: 1, size: 10 },
  filters: Record<string, any> = {}
) {
  return useQuery<ApiResponse<PaginatedResponse<GalleryFolderResponse>>, Error>({
    queryKey: galleryKeys.filter(params, filters),
    queryFn: () => galleryService.filterGalleries(params, filters)
  })
}

// Hook to fetch a single gallery by ID
export function useGallery(galleryId: string) {
  return useQuery<ApiResponse<GalleryDetail>, Error>({
    queryKey: galleryKeys.detail(galleryId),
    queryFn: () => galleryService.getGalleryById(galleryId),
    enabled: !!galleryId
  })
}

// Hook to fetch folder details
export function useGalleryFolder(folderId: string) {
  return useQuery<ApiResponse<GalleryFolderResponse>, Error>({
    queryKey: galleryKeys.folder(folderId),
    queryFn: () => galleryService.getFolderDetails(folderId),
    enabled: !!folderId
  })
}

// Hook to fetch gallery stats
export function useGalleryStats() {
  return useQuery<ApiResponse<GalleryStats>, Error>({
    queryKey: galleryKeys.stats(),
    queryFn: () => galleryService.getGalleryStats()
  })
}

// Hook to create a gallery
export function useCreateGallery() {
  const queryClient = useQueryClient()

  return useMutation<ApiResponse<Gallery>, Error, {
    folderId: string;
    title: string;
    photoUrls: string[];
    isKeyHighlight: boolean;
    isMultimedia: boolean;
    status: 'DRAFT' | 'PUBLISH';
  }>({
    mutationFn: (data) => galleryService.createGallery(data),
    onSuccess: (_, variables) => {
      // Invalidate gallery queries
      queryClient.invalidateQueries({ queryKey: galleryKeys.lists() })
      queryClient.invalidateQueries({ queryKey: galleryKeys.filters() })
      queryClient.invalidateQueries({ queryKey: galleryKeys.stats() })
      queryClient.invalidateQueries({ queryKey: galleryKeys.folders() })

      // Invalidate specific folder to refetch its galleries
      queryClient.invalidateQueries({ queryKey: galleryKeys.folder(variables.folderId) })
    }
  })
}

// Hook to update a gallery
export function useUpdateGallery() {
  const queryClient = useQueryClient()

  return useMutation<
    ApiResponse<Gallery>,
    Error,
    { galleryId: string; data: UpdateGallery }
  >({
    mutationFn: ({ galleryId, data }) =>
      galleryService.updateGallery(galleryId, data),
    onSuccess: (_, { galleryId }) => {
      queryClient.invalidateQueries({ queryKey: galleryKeys.detail(galleryId) })
      queryClient.invalidateQueries({ queryKey: galleryKeys.lists() })
      queryClient.invalidateQueries({ queryKey: galleryKeys.filters() })
      queryClient.invalidateQueries({ queryKey: galleryKeys.folders() })
    }
  })
}

// Hook to delete a gallery
export function useDeleteGallery() {
  const queryClient = useQueryClient()

  return useMutation<ApiResponse<null>, Error, string>({
    mutationFn: (galleryId: string) => galleryService.deleteGallery(galleryId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: galleryKeys.lists() })
      queryClient.invalidateQueries({ queryKey: galleryKeys.filters() })
      queryClient.invalidateQueries({ queryKey: galleryKeys.stats() })
      queryClient.invalidateQueries({ queryKey: galleryKeys.folders() })
    }
  })
}

// Hook to delete a photo
export function useDeletePhoto() {
  const queryClient = useQueryClient()

  return useMutation<ApiResponse<null>, Error, string>({
    mutationFn: (photoId: string) => galleryService.deletePhoto(photoId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: galleryKeys.details() })
      queryClient.invalidateQueries({ queryKey: galleryKeys.lists() })
      queryClient.invalidateQueries({ queryKey: galleryKeys.filters() })
      queryClient.invalidateQueries({ queryKey: galleryKeys.folders() })
    }
  })
}

// Hook to update gallery status
export function useUpdateGalleryStatus() {
  const queryClient = useQueryClient()

  return useMutation<
    ApiResponse<null>,
    Error,
    { galleryId: string; data: UpdateGalleryStatus }
  >({
    mutationFn: ({ galleryId, data }) =>
      galleryService.updateGalleryStatus(galleryId, data),
    onSuccess: (_, { galleryId }) => {
      queryClient.invalidateQueries({ queryKey: galleryKeys.detail(galleryId) })
      queryClient.invalidateQueries({ queryKey: galleryKeys.lists() })
      queryClient.invalidateQueries({ queryKey: galleryKeys.filters() })
      queryClient.invalidateQueries({ queryKey: galleryKeys.stats() })
      queryClient.invalidateQueries({ queryKey: galleryKeys.folders() })
    }
  })
}

// Hook to add photos to gallery
export function useAddPhotoToGallery() {
  const queryClient = useQueryClient()

  return useMutation<
    ApiResponse<null>,
    Error,
    { galleryId: string; photoUrls: string[] }
  >({
    mutationFn: ({ galleryId, photoUrls }) =>
      galleryService.addPhotoToGallery(galleryId, photoUrls),
    onSuccess: (_, { galleryId }) => {
      queryClient.invalidateQueries({ queryKey: galleryKeys.detail(galleryId) })
      queryClient.invalidateQueries({ queryKey: galleryKeys.lists() })
      queryClient.invalidateQueries({ queryKey: galleryKeys.filters() })
      queryClient.invalidateQueries({ queryKey: galleryKeys.folders() })
    }
  })
}
